// plugins/api.ts
export default defineNuxtPlugin(async (nuxtApp) => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase

    const serverCookie = import.meta.server
        ? useRequestHeaders(['cookie']).cookie
        : undefined

    const getLocale = () =>
        ((nuxtApp as any).$i18n?.locale?.value as string) ||
        (nuxtApp.$i18n as any)?.locale ||
        (nuxtApp.$i18n as any)?.defaultLocale ||
        'sl'

    // --- CSRF helpers (NEW) ---
    const xsrfCookie = () => {
        const c = useCookie<string | null>('XSRF-TOKEN', { watch: false })
        return c.value ? decodeURIComponent(c.value) : ''
    }
    const ensureCsrf = async () => {
        if (import.meta.client && !xsrfCookie()) {
            await $fetch('/sanctum/csrf-cookie', {
                baseURL: apiBase,
                credentials: 'include',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
        }
    }
    // fetch CSRF cookie once (no-op on server)
    await ensureCsrf()

    const toHeaderObject = (h: HeadersInit | undefined): Record<string, string> => {
        if (!h) return {}
        if (h instanceof Headers) {
            const o: Record<string, string> = {}
            h.forEach((v, k) => (o[k] = v))
            return o
        }
        return Array.isArray(h) ? Object.fromEntries(h) as Record<string, string> : (h as Record<string, string>)
    }

    type H = HeadersInit
    const ensureHeader = (options: { headers?: H }, key: string, val: string) => {
        const h = options.headers
        if (h instanceof Headers) {
            h.set(key, val)
            return
        }
        const obj: Record<string, string> = Array.isArray(h) ? Object.fromEntries(h) : (h as Record<string, string> ?? {})
        obj[key] = val
        options.headers = obj as H // <- cast back to HeadersInit
    }

    const mergeHeaders = (options: { headers?: H }, extra: Record<string, string>) => {
        for (const [k, v] of Object.entries(extra)) ensureHeader(options, k, v)
    }

    const api = $fetch.create({
        baseURL: apiBase,
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },

        async onRequest({ options, request }) {
            const loc = getLocale()

            mergeHeaders(options, {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept-Language': loc,
                'X-Locale': loc,
            })

            if (import.meta.server && serverCookie) {
                ensureHeader(options, 'cookie', serverCookie)
            }

            const method = (options.method || (typeof request === 'string' ? 'GET' : 'GET')).toUpperCase()
            if (['POST','PUT','PATCH','DELETE'].includes(method)) {
                await ensureCsrf()
                const token = xsrfCookie()
                if (token) ensureHeader(options, 'X-XSRF-TOKEN', token)
            }

            const params = (options.params as Record<string, any> | undefined) ?? {}
            if (!('locale' in params)) params.locale = loc
            options.params = params
        },


        async onResponseError({ response, request, options }) {
            if (response.status === 419 && import.meta.client) {
                await $fetch('/sanctum/csrf-cookie', {
                    baseURL: apiBase,
                    credentials: 'include',
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                })

                // turn current headers into a POJO and add XSRF
                const toObj = (h: HeadersInit | undefined): Record<string, string> => {
                    if (!h) return {}
                    if (h instanceof Headers) {
                        const o: Record<string, string> = {}
                        h.forEach((v, k) => (o[k] = v))
                        return o
                    }
                    return Array.isArray(h) ? Object.fromEntries(h) as Record<string, string> : (h as Record<string, string>)
                }
                const retryHeaders = toObj(options.headers)
                const token = xsrfCookie()
                if (token) retryHeaders['X-XSRF-TOKEN'] = token

                return $fetch(String(request), {
                    ...(options as any),
                    baseURL: apiBase,
                    headers: retryHeaders,      // POJO avoids type issues here
                    credentials: 'include',
                    method: (options.method || 'GET') as any,
                } as any)
            }
        }
    })

    return { provide: { api } }
})
